// Classic canvas chart (fallback when the TradingView library is unavailable or the user prefers it).
import React, { useEffect, useRef, useState } from 'react';
import { api } from './api.js';
import { meta, num } from './format.js';
import { t, tErr } from './i18n.js';
import { useTheme } from './theme.js';

const INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d'];
const INTERVAL_MS = { '1m': 60e3, '5m': 300e3, '15m': 900e3, '1h': 3600e3, '4h': 14400e3, '1d': 86400e3 };
const DARK = { up: '#1FC68A', down: '#F2495C', grid: '#151A23', text: '#8D96A8', bg: '#0A0D12', warn: '#E8B04A', accent: '#8B9CFF',
  volUp: 'rgba(31,198,138,0.25)', volDown: 'rgba(242,73,92,0.25)', tagBg: '#10141B', breachBg: '#3A2F17', entryBg: '#1B2140', entryFg: '#C9D1FF',
  lastFg: '#06120D', cross: 'rgba(141,150,168,0.45)', crossBg: '#2A3240', crossFg: '#E7EAF0' };
const LIGHT = { up: '#0E9F6E', down: '#E0364B', grid: '#EEF1F5', text: '#5A6476', bg: '#FFFFFF', warn: '#B7791F', accent: '#4F5FE0',
  volUp: 'rgba(14,159,110,0.25)', volDown: 'rgba(224,54,75,0.25)', tagBg: '#FFFFFF', breachBg: '#FBF1DE', entryBg: '#E6E9FB', entryFg: '#3442B8',
  lastFg: '#FFFFFF', cross: 'rgba(90,100,118,0.4)', crossBg: '#E9EDF3', crossFg: '#1A2130' };
const palette = () => (document.documentElement.dataset.theme === 'light' ? LIGHT : DARK);
const AXIS_W = 74, TIME_H = 24;

export default function ChartClassic({ symbol, lastPrice, position, breachPrice, breachLabel, toolbarExtra = null }) {
  const [interval, setIntervalName] = useState('15m');
  const [hover, setHover] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const st = useRef({ candles: [], visible: 90, offset: 0, drag: null, mouse: null, dirty: true });
  const overlay = useRef({});
  overlay.current = { lastPrice, position, breachPrice, breachLabel, symbol };

  // Load history on symbol / interval change.
  useEffect(() => {
    let alive = true;
    st.current.candles = []; st.current.offset = 0; st.current.dirty = true;
    setLoadError(null);
    api('GET', `/api/candles?symbol=${symbol}&interval=${interval}`)
      .then((c) => { if (alive) { st.current.candles = c; st.current.dirty = true; } })
      .catch((e) => alive && setLoadError(e.message));
    return () => { alive = false; };
  }, [symbol, interval]);

  // Live price -> update/append the last candle.
  useEffect(() => {
    const s = st.current;
    if (lastPrice == null || !s.candles.length) return;
    const ms = INTERVAL_MS[interval];
    const bucket = Math.floor(Date.now() / ms) * ms;
    const last = s.candles[s.candles.length - 1];
    if (bucket > last.t) s.candles.push({ t: bucket, o: last.c, h: Math.max(last.c, lastPrice), l: Math.min(last.c, lastPrice), c: lastPrice, v: 0 });
    else { last.c = lastPrice; last.h = Math.max(last.h, lastPrice); last.l = Math.min(last.l, lastPrice); }
    s.dirty = true;
  }, [lastPrice, interval]);

  useEffect(() => { st.current.dirty = true; }, [position, breachPrice]);
  const theme = useTheme();
  useEffect(() => { st.current.dirty = true; }, [theme]);

  // Render loop.
  useEffect(() => {
    let raf;
    const canvas = canvasRef.current;
    const ro = new ResizeObserver(() => { st.current.dirty = true; });
    ro.observe(wrapRef.current);
    const frame = () => {
      raf = requestAnimationFrame(frame);
      const s = st.current;
      if (!s.dirty) return;
      s.dirty = false;
      draw(canvas, wrapRef.current, s, overlay.current, interval, setHover);
    };
    frame();
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [interval]);

  const onWheel = (e) => {
    const s = st.current;
    s.visible = Math.max(20, Math.min(300, Math.round(s.visible * (e.deltaY > 0 ? 1.1 : 0.9))));
    s.dirty = true;
  };
  const onMouseDown = (e) => { st.current.drag = { x: e.clientX, offset: st.current.offset }; };
  const onMouseUp = () => { st.current.drag = null; };
  const onMouseMove = (e) => {
    const s = st.current;
    const r = canvasRef.current.getBoundingClientRect();
    s.mouse = { x: e.clientX - r.left, y: e.clientY - r.top };
    if (s.drag) {
      const plotW = r.width - AXIS_W;
      const per = plotW / s.visible;
      s.offset = Math.max(0, Math.min(s.candles.length - 20, s.drag.offset + Math.round((e.clientX - s.drag.x) / per)));
    }
    s.dirty = true;
  };
  const onLeave = () => { st.current.mouse = null; st.current.drag = null; st.current.dirty = true; setHover(null); };

  const h = hover;
  const dp = meta(symbol).priceDp;
  return (
    <section className="chart" aria-label={t('Price chart')}>
      <div className="chart-toolbar">
        {INTERVALS.map((i) => (
          <button key={i} className={'tf' + (i === interval ? ' on' : '')} onClick={() => setIntervalName(i)}>{i.toUpperCase().replace('M', 'm')}</button>
        ))}
        <div className="grow" />
        {toolbarExtra}
        {h && (
          <span className="ohlc">
            O <b>{num(h.o, dp)}</b> H <b>{num(h.h, dp)}</b> L <b>{num(h.l, dp)}</b> C <b className={h.c >= h.o ? 'up' : 'down'}>{num(h.c, dp)}</b>
          </span>
        )}
      </div>
      <div className="chart-canvas" ref={wrapRef}>
        <canvas ref={canvasRef} onWheel={onWheel} onMouseDown={onMouseDown} onMouseUp={onMouseUp} onMouseMove={onMouseMove} onMouseLeave={onLeave} />
        {loadError && <div className="chart-msg">{t('Chart history unavailable: {error}', { error: tErr(loadError) })}</div>}
      </div>
    </section>
  );
}

function niceStep(range, target) {
  const raw = range / target;
  const p = 10 ** Math.floor(Math.log10(raw));
  const n = raw / p;
  return (n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10) * p;
}

function draw(canvas, wrap, s, ov, interval, setHover) {
  const W = wrap.clientWidth, H = wrap.clientHeight;
  if (!W || !H) return;
  const dpr = window.devicePixelRatio || 1;
  if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
    canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
  }
  const ctx = canvas.getContext('2d');
  const C = palette();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
  const all = s.candles;
  if (!all.length) return;

  const plotW = W - AXIS_W, plotH = H - TIME_H;
  const end = all.length - s.offset;
  const start = Math.max(0, end - s.visible);
  const vis = all.slice(start, end);
  const per = plotW / s.visible;
  const bodyW = Math.max(1, Math.min(14, per * 0.7));
  const dp = meta(ov.symbol).priceDp;

  let hi = -Infinity, lo = Infinity, vmax = 0;
  for (const c of vis) { hi = Math.max(hi, c.h); lo = Math.min(lo, c.l); vmax = Math.max(vmax, c.v); }
  if (ov.position) { hi = Math.max(hi, ov.position.entryPrice); lo = Math.min(lo, ov.position.entryPrice); }
  const span0 = hi - lo || hi * 0.01;
  const breachInView = ov.breachPrice != null && ov.breachPrice > lo - span0 * 0.6 && ov.breachPrice < hi + span0 * 0.6;
  if (breachInView) { hi = Math.max(hi, ov.breachPrice); lo = Math.min(lo, ov.breachPrice); }
  const pad = (hi - lo) * 0.08 || hi * 0.002;
  hi += pad; lo -= pad;
  const volH = plotH * 0.16;
  const priceH = plotH - volH - 6;
  const y = (p) => 8 + ((hi - p) / (hi - lo)) * (priceH - 8);
  const xOf = (i) => plotW - (vis.length - i - 0.5) * per;

  // Grid + price axis
  ctx.font = '11px "Geist Mono", ui-monospace, monospace';
  ctx.textBaseline = 'middle';
  const step = niceStep(hi - lo, 6);
  for (let p = Math.ceil(lo / step) * step; p <= hi; p += step) {
    const yy = Math.round(y(p)) + 0.5;
    ctx.strokeStyle = C.grid; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(plotW, yy); ctx.stroke();
    ctx.fillStyle = C.text; ctx.textAlign = 'left';
    ctx.fillText(num(p, step < 1 ? dp : Math.min(dp, 1)), plotW + 8, yy);
  }
  // Time axis
  ctx.textAlign = 'center';
  const every = Math.max(1, Math.round(110 / per));
  const ms = INTERVAL_MS[interval];
  for (let i = 0; i < vis.length; i++) {
    if ((start + i) % every) continue;
    const d = new Date(vis[i].t);
    const label = ms >= 86400e3 ? `${d.getMonth() + 1}/${d.getDate()}` : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    if (xOf(i) < 22 || xOf(i) > plotW - 22) continue;
    ctx.fillStyle = C.text; ctx.fillText(label, xOf(i), plotH + TIME_H / 2);
  }

  // Candles + volume
  for (let i = 0; i < vis.length; i++) {
    const c = vis[i];
    const up = c.c >= c.o;
    const x = xOf(i);
    ctx.fillStyle = up ? C.up : C.down;
    ctx.fillRect(Math.round(x) , Math.round(y(c.h)), 1, Math.max(1, Math.round(y(c.l) - y(c.h))));
    const top = y(Math.max(c.o, c.c)), bot = y(Math.min(c.o, c.c));
    ctx.fillRect(Math.round(x - bodyW / 2), Math.round(top), Math.round(bodyW), Math.max(1, Math.round(bot - top)));
    if (vmax > 0) {
      const vh = (c.v / vmax) * volH;
      ctx.fillStyle = up ? C.volUp : C.volDown;
      ctx.fillRect(Math.round(x - bodyW / 2), Math.round(plotH - vh), Math.round(bodyW), Math.round(vh));
    }
  }

  const hLine = (p, color, dash, label, labelBg, labelFg) => {
    const yy = Math.round(y(p)) + 0.5;
    ctx.save();
    ctx.strokeStyle = color; ctx.setLineDash(dash); ctx.globalAlpha = 0.8;
    ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(plotW, yy); ctx.stroke();
    ctx.restore();
    ctx.fillStyle = labelBg; ctx.fillRect(plotW + 1, yy - 10, AXIS_W - 2, 20);
    ctx.fillStyle = labelFg; ctx.textAlign = 'left'; ctx.fillText(label, plotW + 7, yy);
  };
  const tag = (p, text, color) => {
    const yy = Math.round(y(p));
    ctx.font = '600 11px "Geist", system-ui, sans-serif';
    const w = ctx.measureText(text).width + 16;
    ctx.fillStyle = C.tagBg; ctx.strokeStyle = color; ctx.lineWidth = 1;
    ctx.fillRect(10, yy - 11, w, 22); ctx.strokeRect(10.5, yy - 10.5, w - 1, 21);
    ctx.fillStyle = color; ctx.textAlign = 'left'; ctx.fillText(text, 18, yy);
    ctx.font = '11px "Geist Mono", ui-monospace, monospace';
  };

  // Daily-loss breach level
  if (ov.breachPrice != null) {
    if (breachInView) {
      hLine(ov.breachPrice, C.warn, [4, 4], num(ov.breachPrice, dp), C.breachBg, C.warn);
      tag(ov.breachPrice, ov.breachLabel || t('Loss limit'), C.warn);
    }
  }
  // Position entry
  if (ov.position) {
    const p = ov.position;
    hLine(p.entryPrice, C.accent, [], num(p.entryPrice, dp), C.entryBg, C.entryFg);
    const sign = p.unrealizedPnl >= 0 ? '+' : '-';
    tag(p.entryPrice, t('{side} {qty}  {pnl}', { side: p.side === 'long' ? t('LONG') : t('SHORT'), qty: p.qty, pnl: `${sign}$${num(Math.abs(p.unrealizedPnl), 2)}` }), p.unrealizedPnl >= 0 ? C.up : C.down);
  }
  // Last price
  const last = vis[vis.length - 1];
  if (last && s.offset === 0) {
    const up = last.c >= last.o;
    hLine(last.c, up ? C.up : C.down, [2, 3], num(last.c, dp), up ? C.up : C.down, C.lastFg);
  }

  // Crosshair
  if (s.mouse && s.mouse.x < plotW && s.mouse.y < plotH) {
    const i = Math.max(0, Math.min(vis.length - 1, Math.floor(vis.length - (plotW - s.mouse.x) / per)));
    const c = vis[i];
    ctx.save();
    ctx.strokeStyle = C.cross; ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(Math.round(xOf(i)) + 0.5, 0); ctx.lineTo(Math.round(xOf(i)) + 0.5, plotH); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, Math.round(s.mouse.y) + 0.5); ctx.lineTo(plotW, Math.round(s.mouse.y) + 0.5); ctx.stroke();
    ctx.restore();
    const pAt = hi - ((s.mouse.y - 8) / (priceH - 8)) * (hi - lo);
    if (s.mouse.y <= priceH) {
      ctx.fillStyle = C.crossBg; ctx.fillRect(plotW + 1, s.mouse.y - 10, AXIS_W - 2, 20);
      ctx.fillStyle = C.crossFg; ctx.textAlign = 'left'; ctx.fillText(num(pAt, dp), plotW + 7, s.mouse.y);
    }
    setHover(c ? { ...c } : null);
  } else if (last) {
    setHover({ ...last });
  }
}
